<template>
  <b-container fluid>
    <div class="iq-card mt-3">
      <div class="iq-card-header d-flex justify-content-between">
        <div class="iq-header-title my-2">
          <h4 class="card-title">Landmarks</h4>
        </div>
        <div class="d-flex align-items-center">
          <button
            type="button"
            class="btn btn-sm btn-outline-mantraz"
            :class="{ active: mapHeightMode === 'full' }"
            @click="toggleMapHeightMode"
          >
            {{ mapHeightMode === 'full' ? 'Default Height' : 'Full Height' }}
          </button>
        </div>
      </div>
      <div class="iq-card-body">
        <b-row>
          <b-col md="12">
            <div class="mb-3 position-relative map-search-wrapper">
              <input
                v-model="mapSearchQuery"
                type="text"
                class="form-control"
                placeholder="Search landmark on map..."
                @focus="showMapSearchDropdown = true"
                @input="onMapSearchInput"
                @blur="onMapSearchBlur"
              >
              <div
                v-if="showMapSearchDropdown && filteredMapLandmarks.length > 0"
                class="map-search-dropdown"
              >
                <button
                  v-for="landmark in filteredMapLandmarks"
                  :key="landmark.lndmId"
                  type="button"
                  class="map-search-item"
                  @mousedown.prevent="selectMapSearchLandmark(landmark)"
                >
                  {{ landmark.lndmName || '-' }}
                </button>
              </div>
            </div>
            <div
              ref="landmarksMapWrapper"
              class="landmarks-map-wrapper"
              :style="{ height: mapHeight }"
            >
              <div id="landmarks-map" class="landmarks-map"></div>
            </div>
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col md="12">
            <b-tabs v-model="activeTabIndex" lazy>
              <b-tab title="Company Landmarks">
                <h5 class="mb-1">Company Landmarks</h5>
                <p class="text-muted mb-3">
                  Landmarks data for: {{ activeCompany && activeCompany.compName ? activeCompany.compName : '-' }}.
                </p>
                <b-row class="mb-3">
                  <b-col md="4">
                    <label>Search Name:</label>
                    <input
                      v-model="filterCompany.lndmName"
                      type="text"
                      class="form-control"
                      placeholder="Search by name"
                      @input="onSearchInput('company')"
                    >
                  </b-col>
                  <b-col md="3">
                    <label>Filter Type:</label>
                    <div class="filter-type-controls">
                      <select
                        class="form-control"
                        v-model="filterCompany.lndmType"
                        @change="onFilterChanged('company')"
                      >
                        <option value="">All Types</option>
                        <option value="port">PORT</option>
                        <option value="jetty">JETTY</option>
                        <option value="sea">SEA</option>
                      </select>
                      <button
                        v-if="hasMultipleCompanies"
                        class="btn btn-sm btn-outline-mantraz change-company-btn"
                        type="button"
                        @click="goToChangeCompany"
                      >
                        Change Company
                      </button>
                    </div>
                  </b-col>
                  <b-col md="5" class="text-md-right d-flex align-items-end justify-content-md-end">
                    <button-options
                      :access="'master_landmark'"
                      :hideAdd="false"
                      :hideDetail="true"
                      :isRefreshEnabled="true"
                      :isRefreshed="isRefreshedCompany"
                      :isLoading="isLoadingCompany"
                      @onAddItemClicked="openAddForm('company')"
                      @onRefreshed="getCompanyData('Refreshed')"
                    />
                  </b-col>
                </b-row>
                <landmark-list
                  :access="'master_landmark'"
                  :data="dataCompany"
                  :loading="isLoadingCompany"
                  :pagination="paginationCompany"
                  :sortBy="sortByCompany"
                  :sortDesc="sortDescCompany"
                  @onActionDelete="openDelete($event, 'company')"
                  @onActionEdit="openEdit"
                  @onActionZoom="zoomToLandmark"
                  @onPageChanged="onPageChanged('company', $event)"
                  @onSortChanged="onSortChanged('company', $event)"
                />
              </b-tab>
              <b-tab title="Global Landmarks">
                <h5 class="mb-1">Global Landmarks</h5>
                <p class="text-muted mb-3">Global default landmarks data</p>
                <b-row class="mb-3">
                  <b-col md="4">
                    <label>Search Name:</label>
                    <input
                      v-model="filterGlobal.lndmName"
                      type="text"
                      class="form-control"
                      placeholder="Search by name"
                      @input="onSearchInput('global')"
                    >
                  </b-col>
                  <b-col md="3">
                    <label>Filter Type:</label>
                    <select class="form-control" v-model="filterGlobal.lndmType" @change="onFilterChanged('global')">
                      <option value="">All Types</option>
                      <option value="port">PORT</option>
                      <option value="jetty">JETTY</option>
                      <option value="sea">SEA</option>
                    </select>
                  </b-col>
                  <b-col md="5" class="text-md-right d-flex align-items-end justify-content-md-end">
                    <button-options
                      :access="'master_landmark'"
                      :hideAdd="!isSuperAdmin"
                      :hideDetail="true"
                      :isRefreshEnabled="true"
                      :isRefreshed="isRefreshedGlobal"
                      :isLoading="isLoadingGlobal"
                      @onAddItemClicked="openAddForm('global')"
                      @onRefreshed="getGlobalData('Refreshed')"
                    />
                  </b-col>
                </b-row>
                <landmark-list
                  :access="'master_landmark'"
                  :data="dataGlobal"
                  :loading="isLoadingGlobal"
                  :pagination="paginationGlobal"
                  :sortBy="sortByGlobal"
                  :sortDesc="sortDescGlobal"
                  @onActionDelete="openDelete($event, 'global')"
                  @onActionEdit="openEdit"
                  @onActionZoom="zoomToLandmark"
                  @onPageChanged="onPageChanged('global', $event)"
                  @onSortChanged="onSortChanged('global', $event)"
                />
              </b-tab>
            </b-tabs>
          </b-col>
        </b-row>
      </div>
    </div>
    <b-modal size="lg" hide-footer no-close-on-esc no-close-on-backdrop v-model="displayForm">
      <landmark-form
        :data="currentLandmark"
        :errors="errors"
        :isSubmit="isSubmit"
        :referenceLandmarks="referenceLandmarks"
        @onSubmitClicked="onSubmitClicked"
        @onCancelAddClicked="onCancel"
      />
    </b-modal>
    <b-modal size="lg" hide-footer no-close-on-esc no-close-on-backdrop v-model="displayDeletion">
      <delete-item
        :isSubmit="isSubmit"
        @onCancelDeletion="displayDeletion = false"
        @onConfirmDeletion="onConfirmDeletion"
      />
    </b-modal>
  </b-container>
</template>

<script>
/* eslint-disable no-undef, new-cap */
import LandmarkSvc from '@/services/LandmarkSvc';
import requestBuilder from '@/util/request.builder';
import ButtonOptions from '@/components/directives/button-options';
import DeleteItem from '@/components/directives/delete-item';
import LandmarkList from '@/components/landmark/List.vue';
import LandmarkForm from '@/components/landmark/Form.vue';

export default {
  name: 'Landmarks',
  components: {
    ButtonOptions,
    DeleteItem,
    LandmarkList,
    LandmarkForm,
  },
  data() {
    return {
      currentLandmark: {},
      dataGlobal: [],
      dataCompany: [],
      paginationGlobal: {
        current: 1,
        pageSize: 20,
        totalSize: 0,
      },
      paginationCompany: {
        current: 1,
        pageSize: 20,
        totalSize: 0,
      },
      displayForm: false,
      displayDeletion: false,
      errors: {},
      isLoadingGlobal: false,
      isLoadingCompany: false,
      isRefreshedGlobal: false,
      isRefreshedCompany: false,
      isSubmit: false,
      sortByGlobal: '',
      sortDescGlobal: false,
      sortByCompany: '',
      sortDescCompany: false,
      filterGlobal: {
        lndmName: '',
        lndmType: '',
      },
      filterCompany: {
        lndmName: '',
        lndmType: '',
      },
      searchTimeoutIds: {
        global: null,
        company: null,
      },
      map: null,
      markerLayer: null,
      markerIcon: null,
      mapHeight: '70vh',
      mapHeightMode: 'default',
      resizeHandler: null,
      activeTabIndex: 0,
      selected: null,
      selectedListType: 'global',
      mapSearchQuery: '',
      showMapSearchDropdown: false,
      mapSearchBlurTimeout: null,
    };
  },
  computed: {
    activeCompany() {
      return this.$store.getters['auth/activeCompany'];
    },
    isSuperAdmin() {
      return this.$store.getters['auth/isSuperAdmin'];
    },
    availableCompanies() {
      return this.$store.getters['auth/availableCompany'] || [];
    },
    hasMultipleCompanies() {
      if (Array.isArray(this.availableCompanies)) {
        return this.availableCompanies.length > 1;
      }
      return Object.keys(this.availableCompanies || {}).length > 1;
    },
    referenceLandmarks() {
      const combined = [...(this.dataCompany || []), ...(this.dataGlobal || [])];
      const byId = new Map();
      combined.forEach((landmark) => {
        if (landmark && landmark.lndmId && !byId.has(landmark.lndmId)) {
          byId.set(landmark.lndmId, landmark);
        }
      });
      return Array.from(byId.values());
    },
    filteredMapLandmarks() {
      const list = this.getActiveLandmarks();
      const query = (this.mapSearchQuery || '').trim().toLowerCase();
      if (!query) {
        return list.slice(0, 10);
      }
      return list
        .filter((landmark) => (landmark.lndmName || '').toLowerCase().includes(query))
        .slice(0, 10);
    },
  },
  mounted() {
    this.getGlobalData();
    this.getCompanyData();
    this.loadLeafletCSS();
    this.$nextTick(() => {
      this.applyMapHeight();
      const map = L.map('landmarks-map').setView([-6.2, 106.816666], 5);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);
      this.map = map;
      this.refreshMapMarkers();
    });
    this.resizeHandler = () => this.updateMapHeight();
    window.addEventListener('resize', this.resizeHandler);
  },
  beforeDestroy() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
    if (this.map) {
      this.map.off();
      this.map.remove();
      this.map = null;
    }
    if (this.mapSearchBlurTimeout) {
      clearTimeout(this.mapSearchBlurTimeout);
      this.mapSearchBlurTimeout = null;
    }
  },
  watch: {
    activeCompany(newCompany, oldCompany) {
      if (!newCompany || (oldCompany && newCompany.compId === oldCompany.compId)) {
        return;
      }
      this.paginationCompany.current = 1;
      this.getCompanyData();
    },
    activeTabIndex() {
      this.refreshMapMarkers();
    },
    mapHeight() {
      this.invalidateMapSize();
    },
  },
  methods: {
    loadLeafletCSS() {
      if (!document.querySelector('link[href="/style/leaflet.css"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '/style/leaflet.css';
        document.head.appendChild(link);
      }
    },
    setMapHeightMode(mode) {
      if (mode !== 'default' && mode !== 'full') {
        return;
      }
      this.mapHeightMode = mode;
      this.applyMapHeight();
    },
    toggleMapHeightMode() {
      const nextMode = this.mapHeightMode === 'full' ? 'default' : 'full';
      this.setMapHeightMode(nextMode);
    },
    applyMapHeight() {
      if (this.mapHeightMode === 'default') {
        this.mapHeight = '50vh';
        return;
      }
      this.updateMapHeight();
    },
    updateMapHeight() {
      if (this.mapHeightMode !== 'full') {
        return;
      }
      const mapWrapper = this.$refs.landmarksMapWrapper;
      if (!mapWrapper) {
        return;
      }
      const rect = mapWrapper.getBoundingClientRect();
      const bottomPadding = 24;
      const minHeight = 400;
      const availableHeight = window.innerHeight - rect.top - bottomPadding;
      const nextHeight = Math.max(availableHeight, minHeight);
      this.mapHeight = `${Math.floor(nextHeight)}px`;
    },
    invalidateMapSize() {
      if (!this.map) {
        return;
      }
      this.$nextTick(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      });
    },
    async getGlobalData(notification) {
      return this.fetchData('global', notification);
    },
    async getCompanyData(notification) {
      return this.fetchData('company', notification);
    },
    async fetchData(listType, notification) {
      try {
        const isCompany = listType === 'company';
        if (isCompany && (!this.activeCompany || !this.activeCompany.compId)) {
          this.dataCompany = [];
          this.paginationCompany.totalSize = 0;
          this.refreshMapMarkers();
          return;
        }
        if (isCompany) {
          this.isLoadingCompany = true;
          this.isRefreshedCompany = true;
        } else {
          this.isLoadingGlobal = true;
          this.isRefreshedGlobal = true;
        }
        const pagination = isCompany ? this.paginationCompany : this.paginationGlobal;
        const filter = isCompany ? this.filterCompany : this.filterGlobal;
        const sortBy = isCompany ? this.sortByCompany : this.sortByGlobal;
        const sortDesc = isCompany ? this.sortDescCompany : this.sortDescGlobal;
        const params = {
          ...requestBuilder.buildFilters(pagination, filter),
        };
        if (sortBy === 'lndmName' || sortBy === 'lndmType') {
          params[`orders[${sortBy}]`] = sortDesc ? 'desc' : 'asc';
        }
        if (isCompany) {
          params.compId = this.activeCompany.compId;
        }
        const response = await LandmarkSvc.landmarks.get({
          params: {
            ...params,
          },
        });
        const payload = response && response.data ? response.data : {};
        const listData = Array.isArray(payload.data) ? payload.data : [];
        if (isCompany) {
          this.dataCompany = listData;
        } else {
          this.dataGlobal = listData;
        }
        this.refreshMapMarkers();
        if (payload.pagination) {
          pagination.current = payload.pagination.page || pagination.current;
          pagination.pageSize = payload.pagination.itemPerPage || pagination.pageSize;
          pagination.totalSize = payload.pagination.totalData || 0;
        }
        if (notification) {
          this.$toast.success(notification);
        }
      } catch (error) {
        console.error('Error fetching landmarks:', error);
        this.$toast.error('Failed to load landmarks.');
      } finally {
        if (listType === 'company') {
          this.isLoadingCompany = false;
          this.isRefreshedCompany = false;
        } else {
          this.isLoadingGlobal = false;
          this.isRefreshedGlobal = false;
        }
      }
    },
    async deleteData(notification, data) {
      this.isSubmit = true;
      try {
        const response = await LandmarkSvc.landmarks.delete(data.lndmId);
        if (response.data && response.data.status && response.data.status.error === 0) {
          await this.$toast.success(notification);
          this.displayDeletion = false;
          if (this.selectedListType === 'company') {
            this.paginationCompany.current = 1;
            this.getCompanyData();
          } else {
            this.paginationGlobal.current = 1;
            this.getGlobalData();
          }
        } else {
          const message = response.data && response.data.status ? response.data.status.message : 'Delete failed';
          this.$toast.error(message);
        }
      } catch (error) {
        this.$toast.error(error.message);
      } finally {
        this.isSubmit = false;
      }
    },
    async onSubmitClicked(form) {
      this.errors = {};
      this.isSubmit = true;
      try {
        const isCompany = this.selectedListType === 'company';
        const payload = {
          lndmName: form.lndmName,
          lndmDescription: form.lndmDescription,
          lndmType: form.lndmType,
          lndmLatitude: form.lndmLatitude,
          lndmLongitude: form.lndmLongitude,
        };
        if (!form.lndmId && isCompany && this.activeCompany && this.activeCompany.compId) {
          payload.lndmCompId = this.activeCompany.compId;
        }
        let response = {};
        if (form.lndmId) {
          response = await LandmarkSvc.landmarks.update(form.lndmId, payload);
        } else {
          response = await LandmarkSvc.landmarks.create(payload);
        }
        if (response.data && response.data.status && response.data.status.error === 0) {
          await Promise.all([this.getGlobalData(), this.getCompanyData()]);
          this.$toast.success('Success');
          this.currentLandmark = {};
          this.displayForm = false;
        } else {
          const message = response.data && response.data.status ? response.data.status.message : 'Save failed';
          if (typeof message === 'object') {
            this.errors = message;
            this.$toast.error('Error found');
          } else {
            this.$toast.error(message);
          }
        }
      } catch (error) {
        if (error.response && error.response.data && error.response.data.status) {
          this.$toast.error(error.response.data.status.message);
        } else {
          this.$toast.error('Error when submitting form');
        }
      } finally {
        this.isSubmit = false;
      }
    },
    onCancel() {
      this.currentLandmark = {};
      this.displayForm = false;
    },
    onConfirmDeletion() {
      this.deleteData('Deleted complete', this.selected);
    },
    openAddForm(listType) {
      this.currentLandmark = {};
      this.selectedListType = listType || 'global';
      this.displayForm = true;
    },
    openDelete(data, listType) {
      this.displayDeletion = true;
      this.selected = data;
      this.selectedListType = listType || 'global';
    },
    openEdit(data) {
      this.displayForm = true;
      this.currentLandmark = { ...data };
    },
    onSearchInput(listType) {
      if (this.searchTimeoutIds[listType]) {
        clearTimeout(this.searchTimeoutIds[listType]);
      }
      this.searchTimeoutIds[listType] = setTimeout(() => {
        this.onFilterChanged(listType);
      }, 300);
    },
    onFilterChanged(listType) {
      if (listType === 'company') {
        this.paginationCompany.current = 1;
        this.getCompanyData();
      } else {
        this.paginationGlobal.current = 1;
        this.getGlobalData();
      }
    },
    onPageChanged(listType, page) {
      if (listType === 'company') {
        this.paginationCompany.current = page;
        this.getCompanyData();
      } else {
        this.paginationGlobal.current = page;
        this.getGlobalData();
      }
    },
    onSortChanged(listType, { sortBy, sortDesc }) {
      if (listType === 'company') {
        this.sortByCompany = sortBy || '';
        this.sortDescCompany = !!sortDesc;
        this.paginationCompany.current = 1;
        this.getCompanyData();
      } else {
        this.sortByGlobal = sortBy || '';
        this.sortDescGlobal = !!sortDesc;
        this.paginationGlobal.current = 1;
        this.getGlobalData();
      }
    },
    onMapSearchInput() {
      this.showMapSearchDropdown = true;
    },
    onMapSearchBlur() {
      this.mapSearchBlurTimeout = setTimeout(() => {
        this.showMapSearchDropdown = false;
      }, 120);
    },
    selectMapSearchLandmark(landmark) {
      if (!landmark) {
        return;
      }
      this.mapSearchQuery = landmark.lndmName || '';
      this.showMapSearchDropdown = false;
      this.zoomToLandmark(landmark);
    },
    goToChangeCompany() {
      this.$router.push({ name: 'CompanyActiveSelection' });
    },
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    getActiveLandmarks() {
      return this.activeTabIndex === 0 ? this.dataCompany : this.dataGlobal;
    },
    refreshMapMarkers() {
      if (!this.map || typeof L === 'undefined') {
        return;
      }
      if (!this.markerLayer) {
        this.markerLayer = L.layerGroup().addTo(this.map);
      }
      if (!this.markerIcon) {
        this.markerIcon = L.icon({
          iconUrl: '/marker/pinpoint.png',
          iconSize: [32, 32],
          iconAnchor: [16, 32],
          popupAnchor: [0, -32],
        });
      }
      this.markerLayer.clearLayers();
      const landmarks = this.getActiveLandmarks();
      const bounds = [];
      landmarks.forEach((landmark) => {
        const lat = parseFloat(landmark.lndmLatitude);
        const lng = parseFloat(landmark.lndmLongitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) {
          return;
        }
        const marker = L.marker([lat, lng], {
          icon: this.markerIcon || undefined,
          landmarkId: landmark.lndmId,
        });
        const name = landmark.lndmName ? this.escapeHtml(landmark.lndmName) : '-';
        const type = landmark.lndmType ? this.escapeHtml(String(landmark.lndmType).toUpperCase()) : '-';
        const description = landmark.lndmDescription ? this.escapeHtml(landmark.lndmDescription) : '-';
        marker.bindPopup(
          `<strong>${name}</strong><br>Type: ${type}<br>Description: ${description}`,
        );
        marker.addTo(this.markerLayer);
        bounds.push([lat, lng]);
      });
      if (bounds.length) {
        this.map.fitBounds(bounds, { padding: [24, 24], maxZoom: 12 });
      }
    },
    zoomToLandmark(landmark) {
      if (!this.map || !landmark) {
        return;
      }
      const mapWrapper = this.$refs.landmarksMapWrapper;
      if (mapWrapper && typeof mapWrapper.scrollIntoView === 'function') {
        mapWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      const lat = parseFloat(landmark.lndmLatitude);
      const lng = parseFloat(landmark.lndmLongitude);
      if (Number.isNaN(lat) || Number.isNaN(lng)) {
        this.$toast.error('Landmark coordinates are invalid.');
        return;
      }
      this.map.setView([lat, lng], 14);
      if (!this.markerLayer) {
        return;
      }
      let matchedMarker = null;
      this.markerLayer.eachLayer((layer) => {
        if (layer && layer.options && layer.options.landmarkId === landmark.lndmId) {
          matchedMarker = layer;
        }
      });
      if (matchedMarker) {
        matchedMarker.openPopup();
      }
    },
  },
};
</script>

<style scoped>
.landmarks-map-wrapper {
  width: 100%;
  min-height: 400px;
  border-radius: 8px;
  overflow: hidden;
}

.landmarks-map {
  width: 100%;
  height: 100%;
}

.filter-type-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.change-company-btn {
  white-space: nowrap;
}

.map-search-wrapper {
  max-width: 420px;
}

.map-search-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1200;
  max-height: 240px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}

.map-search-item {
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  padding: 8px 12px;
  font-size: 13px;
}

.map-search-item:hover {
  background: #f5f7fb;
}
</style>
